// src/Components/Modules/HR/EmployeeProfile.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box, Typography, Paper, Button, Chip, IconButton, Tooltip, Skeleton,
  Alert, Stack, Avatar, TextField, InputAdornment, Tabs, Tab, Divider,
  MenuItem, Select, FormControl, InputLabel, LinearProgress, CircularProgress, Grid,
} from "@mui/material";
import {
  FaUser, FaBriefcase, FaWallet, FaSave, FaArrowLeft, FaEnvelope, FaPhone,
  FaIdCard, FaMapMarkerAlt, FaBuilding, FaCalendarAlt, FaMoneyBillWave,
  FaUniversity, FaEdit, FaTimes, FaCheckCircle, FaUserTie, FaSync,
  FaInfoCircle, FaCalendarCheck, FaDollarSign, FaClock, FaIdBadge, FaTint,
  FaHeartbeat, FaGraduationCap, FaMedal, FaFileAlt, FaPassport,
  FaFileInvoice, FaClipboardList, FaPlus, FaTrash, FaChartLine, FaCopy,
  FaArrowRight, FaHome, FaTasks, FaCamera,
} from "react-icons/fa";
import { styled, keyframes } from "@mui/material/styles";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from "recharts";
import Swal from "sweetalert2";

const fadeIn = keyframes`from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); }`;

// ============== Styled ==============
const MainContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: "#f8fafc",
  minHeight: "100vh",
}));

const Card = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  border: "1px solid #e2e8f0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  backgroundColor: "white",
}));

const StatTile = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  border: "1px solid #e2e8f0",
  backgroundColor: "white",
  transition: "all 0.2s ease",
  "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 6px 16px rgba(0,0,0,0.06)", transform: "translateY(-2px)" },
}));

const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 1.5 } };

const InfoItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  transition: "background 0.2s ease",
  "&:hover": {
    background: "#f8f9ff",
  },
}));

// ============== Small helpers ==============
const SectionTitle = ({ icon: Icon, tint, children, action }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ width: 34, height: 34, borderRadius: 1.25, bgcolor: `${tint}14`, color: tint, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon size={15} />
      </Box>
      <Typography variant="subtitle1" fontWeight={700} color="#1e293b">{children}</Typography>
    </Box>
    {action}
  </Box>
);

const InfoRow = ({ icon: Icon, tint, label, value, copyable, onCopy }) => (
  <Box sx={{ display: "flex", gap: 1.5, p: 1.5, borderRadius: 1.5, transition: "background 0.15s", "&:hover": { bgcolor: "#f8fafc" } }}>
    <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: `${tint}14`, color: tint, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Icon size={15} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="#64748b" sx={{ display: "block" }}>{label}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="body2" fontWeight={600} color="#1e293b" sx={{ wordBreak: "break-word" }}>
          {value || "N/A"}
        </Typography>
        {copyable && value && value !== "N/A" && (
          <Tooltip title="Copy">
            <IconButton size="small" onClick={() => onCopy(value)} sx={{ color: "#94a3b8", p: 0.25 }}>
              <FaCopy size={11} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  </Box>
);

const EmptyNote = ({ text }) => (
  <Typography variant="body2" color="#94a3b8" sx={{ fontStyle: "italic" }}>{text}</Typography>
);

// ============== Main Component ==============
const EmployeeProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEdit = searchParams.get("mode") === "edit";

  const [active, setActive] = useState("personal");
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalData, setOriginalData] = useState({});

  const [newSkill, setNewSkill] = useState("");
  const [newEducation, setNewEducation] = useState({ degree: "", institution: "", year: "" });
  const [newExperience, setNewExperience] = useState({ company: "", position: "", duration: "", description: "" });

  // ✅ Photo upload state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadEmployee();
  }, [id]);

  useEffect(() => {
    if (isEdit && originalData && Object.keys(originalData).length > 0) {
      setHasChanges(JSON.stringify(form) !== JSON.stringify(originalData));
    }
  }, [form, originalData, isEdit]);

  // Clean up object URL on unmount
  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  const loadEmployee = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await AxiosAuthInstance.get(`add_employee/${id}`);
      const employeeData = res.data.employee || {};
      console.log("Photo Path from DB:", employeeData.photo_path);
      setForm(employeeData);
      setOriginalData(employeeData);
    } catch (err) {
      setError("Failed to load employee data");
      Swal.fire({ icon: "error", title: "Error", text: "Failed to load employee profile." });
    } finally {
      setLoading(false);
    }
  };

  const parseJSON = (data) => {
    if (!data) return [];
    if (typeof data === "string") {
      try { return JSON.parse(data); } catch { return []; }
    }
    return Array.isArray(data) ? data : [];
  };

  // ---- Photo upload helpers ----
  const clearPhotoStaging = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      Swal.fire({ icon: "warning", title: "Invalid file", text: "Please choose an image (JPG, PNG, WebP)" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({ icon: "warning", title: "File too large", text: "Maximum photo size is 5MB" });
      return;
    }

    if (isEdit) {
      // Stage it — uploaded together with Save
      if (photoPreview) URL.revokeObjectURL(photoPreview);
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    } else {
      // View mode — upload immediately
      uploadPhotoNow(file);
    }
  };

  // Build multipart body. PHP only parses multipart on POST,
  // so we spoof PUT the standard Laravel way (_method=PUT).
  const buildMultipart = (extraFile) => {
    const fd = new FormData();
    const base = {
      ...form,
      skills: parseJSON(form.skills),
      education: parseJSON(form.education),
      experience: parseJSON(form.experience),
      salary_increment_history: parseJSON(form.salary_increment_history),
    };
    Object.entries(base).forEach(([k, v]) => {
      if (v === null || v === undefined) return;
      if (typeof v === "boolean") fd.append(k, v ? "1" : "0");
      else if (typeof v === "object") fd.append(k, JSON.stringify(v));
      else fd.append(k, v);
    });
    if (extraFile) fd.append("employee_photo", extraFile);
    fd.append("_method", "PUT");
    return fd;
  };

  const uploadPhotoNow = async (file) => {
    setPhotoUploading(true);
    try {
      await AxiosAuthInstance.post(`add_employee/${id}`, buildMultipart(file), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      Swal.fire({ icon: "success", title: "Photo updated!", timer: 1500, showConfirmButton: false });
      await loadEmployee();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Upload failed", text: err.response?.data?.message || "Please try again" });
    } finally {
      setPhotoUploading(false);
    }
  };

  const save = async () => {
    const result = await Swal.fire({
      title: "Save Changes?",
      text: photoFile ? "This will also update the employee photo." : "Are you sure you want to save these changes?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#6366f1",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Save",
    });
    if (!result.isConfirmed) return;

    setSaving(true);
    try {
      if (photoFile) {
        // Multipart POST + _method=PUT (required for file uploads)
        await AxiosAuthInstance.post(`add_employee/${id}`, buildMultipart(photoFile), {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        // Original JSON path
        await AxiosAuthInstance.put(`add_employee/${id}`, {
          ...form,
          skills: parseJSON(form.skills),
          education: parseJSON(form.education),
          experience: parseJSON(form.experience),
          salary_increment_history: parseJSON(form.salary_increment_history),
        });
      }

      setOriginalData(form);
      setHasChanges(false);
      clearPhotoStaging();
      Swal.fire({ icon: "success", title: "Saved!", text: "Profile updated successfully", timer: 1800, showConfirmButton: false });
      navigate(`/hr/employees/${id}`);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.response?.data?.message || "Failed to save changes" });
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    if (dirty) {
      Swal.fire({
        title: "Discard Changes?",
        text: "You have unsaved changes.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        confirmButtonText: "Yes, Discard",
        cancelButtonText: "Keep Editing",
      }).then((r) => {
        if (r.isConfirmed) {
          clearPhotoStaging();
          navigate(`/hr/employees/${id}`);
        }
      });
    } else {
      clearPhotoStaging();
      navigate(`/hr/employees/${id}`);
    }
  };

  const handleInputChange = (field, value) => setForm({ ...form, [field]: value });

  // ---- Skills / Education / Experience handlers ----
  const handleAddSkill = () => {
    if (newSkill.trim()) {
      setForm({ ...form, skills: JSON.stringify([...parseJSON(form.skills), newSkill.trim()]) });
      setNewSkill("");
    }
  };
  const handleRemoveSkill = (i) => setForm({ ...form, skills: JSON.stringify(parseJSON(form.skills).filter((_, x) => x !== i)) });

  const handleAddEducation = () => {
    if (newEducation.degree && newEducation.institution) {
      setForm({ ...form, education: JSON.stringify([...parseJSON(form.education), { ...newEducation }]) });
      setNewEducation({ degree: "", institution: "", year: "" });
    }
  };
  const handleRemoveEducation = (i) => setForm({ ...form, education: JSON.stringify(parseJSON(form.education).filter((_, x) => x !== i)) });

  const handleAddExperience = () => {
    if (newExperience.company && newExperience.position) {
      setForm({ ...form, experience: JSON.stringify([...parseJSON(form.experience), { ...newExperience }]) });
      setNewExperience({ company: "", position: "", duration: "", description: "" });
    }
  };
  const handleRemoveExperience = (i) => setForm({ ...form, experience: JSON.stringify(parseJSON(form.experience).filter((_, x) => x !== i)) });

  // ---- Helpers ----
  const copyText = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      Swal.fire({ icon: "success", title: "Copied!", timer: 1200, showConfirmButton: false, toast: true, position: "top-end" });
    }
  };

  const getInitials = (name) => {
    if (!name) return "EM";
    const p = name.split(" ");
    return p.length >= 2 ? `${p[0][0]}${p[1][0]}`.toUpperCase() : name.substring(0, 2).toUpperCase();
  };

  const getColorFromName = (name) => {
    const colors = ["#6366f1", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#06b6d4", "#3b82f6"];
    if (!name) return colors[0];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return colors[Math.abs(h) % colors.length];
  };

  const formatDate = (d) => {
    if (!d) return "N/A";
    try { return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
    catch { return d; }
  };

  const formatCurrency = (a) => `৳${Number(a || 0).toLocaleString("en-BD", { maximumFractionDigits: 0 })}`;

  const calculateTenure = () => {
    if (!form.joining_date) return "N/A";
    try {
      const start = new Date(form.joining_date);
      const now = new Date();
      const total = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
      if (total < 12) return `${total} month${total !== 1 ? "s" : ""}`;
      const y = Math.floor(total / 12), m = total % 12;
      return `${y} yr${y !== 1 ? "s" : ""}${m > 0 ? ` ${m} mo` : ""}`;
    } catch { return "N/A"; }
  };

  const getLifecycleStatus = (s) => ({ 1: "Active", 2: "Resigned", 3: "Terminated", 4: "Probation" }[s] || "Unknown");

  // ---- Parsed JSON ----
  const skills = parseJSON(form.skills);
  const education = parseJSON(form.education);
  const experience = parseJSON(form.experience);
  const incrementHistory = parseJSON(form.salary_increment_history);

  // ---- Salary donut data ----
  const salaryParts = useMemo(() => {
    const parts = [
      { name: "Basic", value: Number(form.basic_salary) || 0, color: "#6366f1" },
      { name: "House Rent", value: Number(form.house_rent) || 0, color: "#10b981" },
      { name: "Medical", value: Number(form.medical_allowance) || 0, color: "#f59e0b" },
      { name: "Conveyance", value: Number(form.conveyance) || 0, color: "#ec4899" },
    ].filter((p) => p.value > 0);
    const sum = parts.reduce((s, p) => s + p.value, 0);
    const gross = Number(form.gross_salary) || 0;
    if (gross > sum) parts.push({ name: "Other", value: gross - sum, color: "#94a3b8" });
    return parts;
  }, [form]);

  // ---- Profile completeness ----
  const completeness = useMemo(() => {
    const checks = [
      form.employee_name, form.employee_email, form.employee_phone, form.nid,
      form.address, form.blood_group, form.joining_date, form.employee_designation,
      form.department, form.bank_name, form.bank_account_number, form.photo_path,
      skills.length > 0, education.length > 0,
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [form, skills.length, education.length]);

  const dirty = hasChanges || !!photoFile;

  const tabs = [
    { key: "personal", label: "Personal Info", icon: FaUser },
    { key: "job", label: "Job Details", icon: FaBriefcase },
    { key: "financial", label: "Financial & Bank", icon: FaWallet },
    { key: "documents", label: "Skills & Career", icon: FaMedal },
  ];

  // ---- Loading ----
  if (loading) {
    return (
      <MainContainer>
        <Breadcrumb title="Employee Profile" />
        <Card sx={{ mb: 3 }}>
          <Skeleton variant="rectangular" height={110} />
          <Box sx={{ p: 3, display: "flex", gap: 3, alignItems: "center" }}>
            <Skeleton variant="circular" width={100} height={100} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="40%" height={40} />
              <Skeleton width="60%" />
            </Box>
          </Box>
        </Card>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(5, 1fr)" }, gap: 2 }}>
          {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} variant="rectangular" height={90} sx={{ borderRadius: 2 }} />)}
        </Box>
      </MainContainer>
    );
  }

  return (
    <MainContainer sx={{ animation: `${fadeIn} 0.4s ease-out`, pb: isEdit ? 12 : 3 }}>
      <Breadcrumb title="Employee Profile" />

      {/* ==================== HEADER ==================== */}
      <Card sx={{ mb: 3, overflow: "visible" }}>
        {/* Cover banner */}
        <Box sx={{ height: 110, background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 55%, #a855f7 100%)", borderRadius: "16px 16px 0 0", position: "relative" }}>
          <Box sx={{ position: "absolute", top: 12, right: 12 }}>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Go Back">
                <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}>
                  <FaArrowLeft />
                </IconButton>
              </Tooltip>
              {!isEdit && (
                <Tooltip title="Edit Profile">
                  <IconButton onClick={() => navigate(`/hr/employees/${id}?mode=edit`)} sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}>
                    <FaEdit />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Refresh">
                <IconButton onClick={loadEmployee} sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}>
                  <FaSync />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Box>

        <Box sx={{ px: { xs: 2, md: 4 }, pb: 3 }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "center", md: "flex-end" }, gap: 3, mt: -7 }}>
            {/* ✅ Avatar with camera button + photo preview */}
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              <Avatar
                sx={{
                  width: 116, height: 116, fontSize: "2.5rem", fontWeight: 700,
                  border: "5px solid white", boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                  bgcolor: (photoPreview || form.photo_path) ? "transparent" : getColorFromName(form.employee_name),
                }}
              >
                {photoPreview ? (
                  <img src={photoPreview} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : form.photo_path ? (
                  <img
                    src={`${process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000"}/storage/${form.photo_path}`}
                    alt="Profile"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  getInitials(form.employee_name)
                )}
              </Avatar>

              {photoUploading && (
                <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(255,255,255,0.6)", borderRadius: "50%" }}>
                  <CircularProgress size={28} sx={{ color: "#6366f1" }} />
                </Box>
              )}

              <Tooltip title={isEdit ? "Change photo (saves with your changes)" : "Change photo"}>
                <IconButton
                  onClick={() => fileInputRef.current?.click()}
                  disabled={photoUploading || saving}
                  sx={{
                    position: "absolute", bottom: 2, right: 2,
                    width: 34, height: 34,
                    bgcolor: "#6366f1", color: "white",
                    border: "3px solid white",
                    "&:hover": { bgcolor: "#4f46e5" },
                  }}
                >
                  <FaCamera size={13} />
                </IconButton>
              </Tooltip>

              <input type="file" accept="image/*" hidden ref={fileInputRef} onChange={handlePhotoSelect} />
            </Box>

            <Box sx={{ flex: 1, textAlign: { xs: "center", md: "left" }, pb: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: { xs: "center", md: "flex-start" }, flexWrap: "wrap", mb: 0.5 }}>
                <Typography variant="h5" fontWeight={800} color="#1e293b">
                  {form.employee_name || "Employee"}
                </Typography>
                <Chip
                  icon={form.employee_status ? <FaCheckCircle /> : <FaTimes />}
                  label={form.employee_status ? "Active" : "Inactive"}
                  size="small"
                  sx={{
                    bgcolor: form.employee_status ? "#ecfdf5" : "#fef2f2",
                    color: form.employee_status ? "#059669" : "#dc2626",
                    fontWeight: 700,
                    "& .MuiChip-icon": { color: form.employee_status ? "#059669" : "#dc2626" },
                  }}
                />
                <Chip label={getLifecycleStatus(form.lifecycle_status)} size="small" sx={{ bgcolor: "#eef2ff", color: "#4338ca", fontWeight: 700 }} />
                {form.employment_type && (
                  <Chip label={form.employment_type} size="small" sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 600, textTransform: "capitalize" }} />
                )}
                {isEdit && <Chip icon={<FaEdit />} label="Editing" size="small" sx={{ bgcolor: "#fffbeb", color: "#b45309", fontWeight: 700 }} />}
                {isEdit && photoFile && (
                  <Chip icon={<FaCamera />} label="New photo — saves on Save" size="small"
                    sx={{ bgcolor: "#eef2ff", color: "#4338ca", fontWeight: 700 }} />
                )}
              </Box>
              <Typography variant="body1" color="#64748b" fontWeight={600}>
                {form.employee_designation || "Designation"} • {form.department || "Department"}
              </Typography>
              <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap justifyContent={{ xs: "center", md: "flex-start" }} sx={{ mt: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "#64748b" }}>
                  <FaEnvelope size={12} />
                  <Typography variant="body2">{form.employee_email || "—"}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "#64748b" }}>
                  <FaPhone size={12} />
                  <Typography variant="body2">{form.employee_phone || "—"}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "#64748b" }}>
                  <FaCalendarCheck size={12} />
                  <Typography variant="body2">Joined {formatDate(form.joining_date)}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "#64748b" }}>
                  <FaClock size={12} />
                  <Typography variant="body2">{calculateTenure()}</Typography>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Card>

      {/* ==================== STAT TILES ==================== */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(5, 1fr)" }, gap: 2, mb: 3 }}>
        <StatTile>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: 1.25, bgcolor: "#eff6ff", color: "#1e40af", display: "flex", alignItems: "center", justifyContent: "center" }}><FaBuilding size={15} /></Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="#64748b">Department</Typography>
              <Typography variant="subtitle2" fontWeight={700} color="#1e293b" noWrap>{form.department || "N/A"}</Typography>
            </Box>
          </Box>
        </StatTile>
        <StatTile>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: 1.25, bgcolor: "#faf5ff", color: "#7c3aed", display: "flex", alignItems: "center", justifyContent: "center" }}><FaUserTie size={15} /></Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="#64748b">Designation</Typography>
              <Typography variant="subtitle2" fontWeight={700} color="#1e293b" noWrap>{form.employee_designation || "N/A"}</Typography>
            </Box>
          </Box>
        </StatTile>
        <StatTile>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: 1.25, bgcolor: "#ecfdf5", color: "#059669", display: "flex", alignItems: "center", justifyContent: "center" }}><FaCalendarCheck size={15} /></Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="#64748b">Tenure</Typography>
              <Typography variant="subtitle2" fontWeight={700} color="#1e293b" noWrap>{calculateTenure()}</Typography>
            </Box>
          </Box>
        </StatTile>
        <StatTile>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: 1.25, bgcolor: "#fffbeb", color: "#b45309", display: "flex", alignItems: "center", justifyContent: "center" }}><FaMoneyBillWave size={15} /></Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="#64748b">Gross Salary</Typography>
              <Typography variant="subtitle2" fontWeight={700} color="#1e293b" noWrap>{formatCurrency(form.gross_salary)}</Typography>
            </Box>
          </Box>
        </StatTile>
        <StatTile>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: 1.25, bgcolor: "#eef2ff", color: "#4338ca", display: "flex", alignItems: "center", justifyContent: "center" }}><FaTasks size={15} /></Box>
            <Box>
              <Typography variant="caption" color="#64748b">Profile Completeness</Typography>
              <Typography variant="subtitle2" fontWeight={700} color="#1e293b">{completeness}%</Typography>
            </Box>
          </Box>
          <LinearProgress variant="determinate" value={completeness}
            sx={{ height: 6, borderRadius: 3, bgcolor: "#e2e8f0", "& .MuiLinearProgress-bar": { borderRadius: 3, bgcolor: completeness > 75 ? "#10b981" : completeness > 50 ? "#f59e0b" : "#ef4444" } }} />
        </StatTile>
      </Box>

      {/* ==================== TABS ==================== */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={active} onChange={(e, v) => setActive(v)} variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: "1px solid #e2e8f0", "& .MuiTabs-indicator": { bgcolor: "#6366f1", height: 3, borderRadius: "3px 3px 0 0" } }}>
          {tabs.map((t) => (
            <Tab key={t.key} value={t.key} icon={<t.icon size={14} />} iconPosition="start" label={t.label}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: 13, minHeight: 52, color: "#64748b", "&.Mui-selected": { color: "#6366f1" } }} />
          ))}
        </Tabs>
      </Card>

      {/* ==================== PERSONAL TAB ==================== */}
      {active === "personal" && (
        <Card sx={{ p: 3, mb: 3, animation: `${fadeIn} 0.3s ease-out` }}>
          <SectionTitle icon={FaUser} tint="#6366f1">Personal Information</SectionTitle>
          {isEdit ? (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 2 }}>
              <TextField size="small" fullWidth label="Full Name" value={form.employee_name || ""} onChange={(e) => handleInputChange("employee_name", e.target.value)} sx={inputSx} />
              <TextField size="small" fullWidth label="Email Address" type="email" value={form.employee_email || ""} onChange={(e) => handleInputChange("employee_email", e.target.value)} sx={inputSx} />
              <TextField size="small" fullWidth label="Phone Number" value={form.employee_phone || ""} onChange={(e) => handleInputChange("employee_phone", e.target.value)} sx={inputSx} />
              <TextField size="small" fullWidth label="National ID (NID)" value={form.nid || ""} onChange={(e) => handleInputChange("nid", e.target.value)} sx={inputSx} />
              <TextField size="small" fullWidth label="Passport Number" value={form.nid_passport_number || ""} onChange={(e) => handleInputChange("nid_passport_number", e.target.value)} sx={inputSx} />
              <FormControl size="small" fullWidth sx={inputSx}>
                <InputLabel>Blood Group</InputLabel>
                <Select value={form.blood_group || ""} label="Blood Group" onChange={(e) => handleInputChange("blood_group", e.target.value)}>
                  {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((bg) => <MenuItem key={bg} value={bg}>{bg}</MenuItem>)}
                </Select>
              </FormControl>
              <Box sx={{ gridColumn: "1 / -1" }}>
                <TextField size="small" fullWidth label="Address" value={form.address || ""} onChange={(e) => handleInputChange("address", e.target.value)} multiline rows={2} sx={inputSx} />
              </Box>
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" fontWeight={700} color="#dc2626" sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                  <FaHeartbeat size={13} /> Emergency Contact
                </Typography>
              </Box>
              <TextField size="small" fullWidth label="Emergency Contact Name" value={form.emergency_contact_name || ""} onChange={(e) => handleInputChange("emergency_contact_name", e.target.value)} sx={inputSx} />
              <TextField size="small" fullWidth label="Emergency Contact Phone" value={form.emergency_contact_phone || ""} onChange={(e) => handleInputChange("emergency_contact_phone", e.target.value)} sx={inputSx} />
              <FormControl size="small" fullWidth sx={inputSx}>
                <InputLabel>Relation</InputLabel>
                <Select value={form.emergency_contact_relation || ""} label="Relation" onChange={(e) => handleInputChange("emergency_contact_relation", e.target.value)}>
                  {["Father", "Mother", "Spouse", "Sibling", "Child", "Friend", "Other"].map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          ) : (
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 1 }}>
                <InfoRow icon={FaUser} tint="#6366f1" label="Full Name" value={form.employee_name} copyable onCopy={copyText} />
                <InfoRow icon={FaEnvelope} tint="#8b5cf6" label="Email Address" value={form.employee_email} copyable onCopy={copyText} />
                <InfoRow icon={FaPhone} tint="#10b981" label="Phone Number" value={form.employee_phone} copyable onCopy={copyText} />
                <InfoRow icon={FaIdCard} tint="#f59e0b" label="National ID (NID)" value={form.nid} copyable onCopy={copyText} />
                <InfoRow icon={FaPassport} tint="#0d9488" label="Passport Number" value={form.nid_passport_number} copyable onCopy={copyText} />
                <InfoRow icon={FaTint} tint="#dc2626" label="Blood Group" value={form.blood_group} />
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <InfoRow icon={FaMapMarkerAlt} tint="#db2777" label="Address" value={form.address} />
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={700} color="#dc2626" sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                <FaHeartbeat size={13} /> Emergency Contact
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1 }}>
                <InfoRow icon={FaUserTie} tint="#6366f1" label="Name" value={form.emergency_contact_name} />
                <InfoRow icon={FaPhone} tint="#10b981" label="Phone" value={form.emergency_contact_phone} copyable onCopy={copyText} />
                <InfoRow icon={FaUser} tint="#f59e0b" label="Relation" value={form.emergency_contact_relation} />
              </Box>
            </>
          )}
        </Card>
      )}

      {/* ==================== JOB TAB ==================== */}
      {active === "job" && (
        <Card sx={{ p: 3, mb: 3, animation: `${fadeIn} 0.3s ease-out` }}>
          <SectionTitle icon={FaBriefcase} tint="#8b5cf6">Job Details</SectionTitle>
          {isEdit ? (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 2 }}>
              <TextField size="small" fullWidth label="Designation" value={form.employee_designation || ""} onChange={(e) => handleInputChange("employee_designation", e.target.value)} sx={inputSx} />
              <TextField size="small" fullWidth label="Department" value={form.department || ""} onChange={(e) => handleInputChange("department", e.target.value)} sx={inputSx} />
              <FormControl size="small" fullWidth sx={inputSx}>
                <InputLabel>Employment Type</InputLabel>
                <Select value={form.employment_type || "permanent"} label="Employment Type" onChange={(e) => handleInputChange("employment_type", e.target.value)}>
                  {["permanent", "contractual", "part_time", "intern", "probation", "temporary", "freelance"].map((t) => (
                    <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth sx={inputSx}>
                <InputLabel>Shift Type</InputLabel>
                <Select value={form.shift_type || "day"} label="Shift Type" onChange={(e) => handleInputChange("shift_type", e.target.value)}>
                  {["day", "night", "rotational", "flexible"].map((s) => (
                    <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)} Shift</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField size="small" fullWidth label="Joining Date" type="date" value={form.joining_date || ""} onChange={(e) => handleInputChange("joining_date", e.target.value)} InputLabelProps={{ shrink: true }} sx={inputSx} />
              <TextField size="small" fullWidth label="Notice Period (Days)" type="number" value={form.notice_period_days || 30} onChange={(e) => handleInputChange("notice_period_days", e.target.value)} sx={inputSx} />
            </Box>
          ) : (
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 1 }}>
                <InfoRow icon={FaUserTie} tint="#6366f1" label="Designation" value={form.employee_designation} />
                <InfoRow icon={FaBuilding} tint="#8b5cf6" label="Department" value={form.department} />
                <Grid item xs={12} md={6}>
                  <InfoItem>
                    <Avatar sx={{ bgcolor: "#e3f2fd", color: "#1976d2", width: 40, height: 40 }}><FaMapMarkerAlt /></Avatar>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Workstation</Typography>
                      <Typography variant="body1" fontWeight={600}>{form.workstation?.name || "N/A"}</Typography>
                      {form.workstation?.address && (
                        <Typography variant="caption" color="text.secondary">{form.workstation.address}</Typography>
                      )}
                    </Box>
                  </InfoItem>
                </Grid>
                <InfoRow icon={FaBriefcase} tint="#10b981" label="Employment Type" value={form.employment_type} />
                <InfoRow icon={FaClock} tint="#f59e0b" label="Shift Type" value={form.shift_type} />
                <InfoRow icon={FaCalendarAlt} tint="#db2777" label="Joining Date" value={formatDate(form.joining_date)} />
                <InfoRow icon={FaClock} tint="#0d9488" label="Notice Period" value={`${form.notice_period_days || 30} Days`} />
              </Box>
              {form.is_probation && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Alert severity="warning" icon={<FaInfoCircle />}>
                    Probation ends on <strong>{formatDate(form.probation_end)}</strong>
                  </Alert>
                </>
              )}
              {(form.employment_type === "contractual" || form.employment_type === "temporary") && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 1 }}>
                    <InfoRow icon={FaFileInvoice} tint="#8b5cf6" label="Contract Start" value={formatDate(form.contract_start)} />
                    <InfoRow icon={FaFileInvoice} tint="#8b5cf6" label="Contract End" value={formatDate(form.contract_end)} />
                  </Box>
                </>
              )}
            </>
          )}
        </Card>
      )}

      {/* ==================== FINANCIAL TAB ==================== */}
      {active === "financial" && (
        <Card sx={{ p: 3, mb: 3, animation: `${fadeIn} 0.3s ease-out` }}>
          <SectionTitle icon={FaWallet} tint="#10b981">Financial & Bank Details</SectionTitle>
          {isEdit ? (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 2 }}>
              <TextField size="small" fullWidth label="Basic Salary" type="number" value={form.basic_salary || ""} onChange={(e) => handleInputChange("basic_salary", e.target.value)} sx={inputSx} />
              <TextField size="small" fullWidth label="Gross Salary" type="number" value={form.gross_salary || ""} onChange={(e) => handleInputChange("gross_salary", e.target.value)} sx={inputSx} />
              <TextField size="small" fullWidth label="Bank Name" value={form.bank_name || ""} onChange={(e) => handleInputChange("bank_name", e.target.value)} sx={inputSx} />
              <TextField size="small" fullWidth label="Bank Account Number" value={form.bank_account_number || ""} onChange={(e) => handleInputChange("bank_account_number", e.target.value)} sx={inputSx} />
              <TextField size="small" fullWidth label="Bank Routing Number" value={form.bank_routing_number || ""} onChange={(e) => handleInputChange("bank_routing_number", e.target.value)} sx={inputSx} />
              <TextField size="small" fullWidth label="TIN Number" value={form.tin_number || ""} onChange={(e) => handleInputChange("tin_number", e.target.value)} helperText="Tax Identification Number" sx={inputSx} />
            </Box>
          ) : (
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" }, gap: 3 }}>
                {/* Salary donut */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="#475569" sx={{ mb: 1 }}>Salary Structure</Typography>
                  {salaryParts.length > 0 ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box sx={{ width: 150, height: 150, position: "relative", flexShrink: 0 }}>
                        <ResponsiveContainer>
                          <PieChart>
                            <Pie data={salaryParts} dataKey="value" innerRadius={48} outerRadius={70} paddingAngle={3}>
                              {salaryParts.map((p, i) => <Cell key={i} fill={p.color} />)}
                            </Pie>
                            <RechartsTooltip formatter={(v) => formatCurrency(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                      <Stack spacing={0.75} sx={{ flex: 1 }}>
                        {salaryParts.map((p, i) => (
                          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: p.color }} />
                            <Typography variant="caption" color="#64748b" sx={{ flex: 1 }}>{p.name}</Typography>
                            <Typography variant="caption" fontWeight={700} color="#1e293b">{formatCurrency(p.value)}</Typography>
                          </Box>
                        ))}
                        <Divider sx={{ my: 0.5 }} />
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="caption" fontWeight={700} color="#475569">Gross Salary</Typography>
                          <Typography variant="subtitle2" fontWeight={800} color="#059669">{formatCurrency(form.gross_salary)}</Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ) : (
                    <EmptyNote text="No salary structure recorded." />
                  )}
                </Box>

                {/* Bank info */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="#475569" sx={{ mb: 1 }}>Bank & Tax</Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1 }}>
                    <InfoRow icon={FaUniversity} tint="#1e40af" label="Bank Name" value={form.bank_name} />
                    <InfoRow icon={FaIdBadge} tint="#10b981" label="Account Number" value={form.bank_account_number} copyable onCopy={copyText} />
                    <InfoRow icon={FaClipboardList} tint="#f59e0b" label="Routing Number" value={form.bank_routing_number} copyable onCopy={copyText} />
                    <InfoRow icon={FaFileInvoice} tint="#8b5cf6" label="TIN Number" value={form.tin_number} copyable onCopy={copyText} />
                  </Box>
                </Box>
              </Box>

              {/* Allowances strip */}
              <Divider sx={{ my: 2.5 }} />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 1.5 }}>
                {[
                  { icon: FaDollarSign, tint: "#6366f1", label: "Basic", value: form.basic_salary },
                  { icon: FaHome, tint: "#10b981", label: "House Rent", value: form.house_rent },
                  { icon: FaHeartbeat, tint: "#f59e0b", label: "Medical", value: form.medical_allowance },
                  { icon: FaBriefcase, tint: "#ec4899", label: "Conveyance", value: form.conveyance },
                ].map((a, i) => (
                  <Box key={i} sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "#f8fafc", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 1.25 }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: 1.25, bgcolor: `${a.tint}14`, color: a.tint, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <a.icon size={14} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="#64748b">{a.label}</Typography>
                      <Typography variant="subtitle2" fontWeight={700} color="#1e293b">{formatCurrency(a.value)}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Card>
      )}

      {/* ==================== SKILLS & CAREER TAB ==================== */}
      {active === "documents" && (
        <Card sx={{ p: 3, mb: 3, animation: `${fadeIn} 0.3s ease-out` }}>
          <SectionTitle icon={FaMedal} tint="#f59e0b">Skills, Education & Experience</SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 3 }}>
            {/* Skills */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="#475569" sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                <FaMedal size={13} color="#f59e0b" /> Skills
              </Typography>
              {isEdit && (
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                  <TextField size="small" fullWidth placeholder="Add a skill, press Enter" value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddSkill(); } }}
                    sx={inputSx} />
                  <Button size="small" variant="contained" onClick={handleAddSkill} startIcon={<FaPlus />}
                    sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
                    Add
                  </Button>
                </Stack>
              )}
              {skills.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {skills.map((s, i) => (
                    <Chip key={i} label={s} size="small"
                      onDelete={isEdit ? () => handleRemoveSkill(i) : undefined}
                      sx={{ bgcolor: "#eef2ff", color: "#4338ca", fontWeight: 600, border: "1px solid #c7d2fe" }} />
                  ))}
                </Box>
              ) : (
                <EmptyNote text="No skills recorded." />
              )}
            </Box>

            {/* Education */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="#475569" sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                <FaGraduationCap size={14} color="#10b981" /> Education
              </Typography>
              {isEdit && (
                <Box sx={{ p: 1.5, mb: 1.5, borderRadius: 1.5, bgcolor: "#f8fafc", border: "1px dashed #cbd5e1" }}>
                  <Stack spacing={1}>
                    <TextField size="small" fullWidth placeholder="Degree" value={newEducation.degree} onChange={(e) => setNewEducation({ ...newEducation, degree: e.target.value })} sx={inputSx} />
                    <TextField size="small" fullWidth placeholder="Institution" value={newEducation.institution} onChange={(e) => setNewEducation({ ...newEducation, institution: e.target.value })} sx={inputSx} />
                    <TextField size="small" fullWidth placeholder="Year" value={newEducation.year} onChange={(e) => setNewEducation({ ...newEducation, year: e.target.value })} sx={inputSx} />
                    <Button size="small" variant="contained" onClick={handleAddEducation} startIcon={<FaPlus />} sx={{ textTransform: "none", fontWeight: 700 }}>Add Education</Button>
                  </Stack>
                </Box>
              )}
              <Stack spacing={1}>
                {education.map((edu, i) => (
                  <Box key={i} sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "#f8fafc", border: "1px solid #e2e8f0", display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: 1.25, bgcolor: "#ecfdf5", color: "#059669", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <FaGraduationCap size={14} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={700} color="#1e293b">{edu.degree}</Typography>
                      <Typography variant="caption" color="#64748b">{edu.institution}{edu.year && ` • ${edu.year}`}</Typography>
                    </Box>
                    {isEdit && <IconButton size="small" onClick={() => handleRemoveEducation(i)} sx={{ color: "#dc2626" }}><FaTrash size={12} /></IconButton>}
                  </Box>
                ))}
                {education.length === 0 && <EmptyNote text="No education recorded." />}
              </Stack>
            </Box>

            {/* Experience timeline */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="#475569" sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                <FaBriefcase size={13} color="#6366f1" /> Experience
              </Typography>
              {isEdit && (
                <Box sx={{ p: 1.5, mb: 1.5, borderRadius: 1.5, bgcolor: "#f8fafc", border: "1px dashed #cbd5e1" }}>
                  <Stack spacing={1}>
                    <TextField size="small" fullWidth placeholder="Company" value={newExperience.company} onChange={(e) => setNewExperience({ ...newExperience, company: e.target.value })} sx={inputSx} />
                    <TextField size="small" fullWidth placeholder="Position" value={newExperience.position} onChange={(e) => setNewExperience({ ...newExperience, position: e.target.value })} sx={inputSx} />
                    <TextField size="small" fullWidth placeholder="Duration (e.g., 2 years)" value={newExperience.duration} onChange={(e) => setNewExperience({ ...newExperience, duration: e.target.value })} sx={inputSx} />
                    <TextField size="small" fullWidth placeholder="Description" multiline rows={2} value={newExperience.description} onChange={(e) => setNewExperience({ ...newExperience, description: e.target.value })} sx={inputSx} />
                    <Button size="small" variant="contained" onClick={handleAddExperience} startIcon={<FaPlus />} sx={{ textTransform: "none", fontWeight: 700 }}>Add Experience</Button>
                  </Stack>
                </Box>
              )}
              {experience.length > 0 ? (
                <Box>
                  {experience.map((exp, i) => (
                    <Box key={i} sx={{ display: "flex", gap: 1.5 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#6366f1", border: "3px solid #e0e7ff", mt: 0.5, flexShrink: 0 }} />
                        {i < experience.length - 1 && <Box sx={{ width: 2, flex: 1, bgcolor: "#e2e8f0", my: 0.5 }} />}
                      </Box>
                      <Box sx={{ pb: 2.5, flex: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="body2" fontWeight={700} color="#1e293b">{exp.position}</Typography>
                          {isEdit && <IconButton size="small" onClick={() => handleRemoveExperience(i)} sx={{ color: "#dc2626" }}><FaTrash size={12} /></IconButton>}
                        </Box>
                        <Typography variant="caption" color="#64748b" fontWeight={600}>{exp.company}{exp.duration && ` • ${exp.duration}`}</Typography>
                        {exp.description && <Typography variant="body2" color="#475569" sx={{ mt: 0.5 }}>{exp.description}</Typography>}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <EmptyNote text="No experience recorded." />
              )}
            </Box>
          </Box>

          {/* Increment history */}
          {incrementHistory.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <SectionTitle icon={FaChartLine} tint="#8b5cf6">Salary Increment History</SectionTitle>
              <Stack spacing={1}>
                {incrementHistory.map((inc, i) => {
                  const pct = inc.old_basic > 0 ? Math.round(((inc.new_basic - inc.old_basic) / inc.old_basic) * 100) : 0;
                  return (
                    <Box key={i} sx={{ p: 1.75, borderRadius: 1.5, bgcolor: "#f8fafc", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: 1.25, bgcolor: "#faf5ff", color: "#7c3aed", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <FaChartLine size={14} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 140 }}>
                        <Typography variant="body2" fontWeight={700} color="#1e293b">{formatDate(inc.date)}</Typography>
                        <Typography variant="caption" color="#64748b">{inc.reason || "Increment"}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" color="#94a3b8" sx={{ textDecoration: "line-through" }}>{formatCurrency(inc.old_basic)}</Typography>
                        <FaArrowRight size={11} color="#94a3b8" />
                        <Typography variant="subtitle2" fontWeight={800} color="#059669">{formatCurrency(inc.new_basic)}</Typography>
                        <Chip label={`+${pct}%`} size="small" sx={{ bgcolor: "#ecfdf5", color: "#059669", fontWeight: 800 }} />
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}
        </Card>
      )}

      {/* ==================== STICKY SAVE BAR ==================== */}
      {isEdit && (
        <Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1100, p: 2, pointerEvents: "none" }}>
          <Paper elevation={6} sx={{
            maxWidth: 900, mx: "auto", p: 1.5, borderRadius: 2, pointerEvents: "auto",
            border: "1px solid #e2e8f0",
            bgcolor: dirty ? "#fffbeb" : "white",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2,
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <FaInfoCircle color={dirty ? "#b45309" : "#94a3b8"} />
              <Typography variant="body2" fontWeight={600} color={dirty ? "#92400e" : "#64748b"} noWrap>
                {dirty ? "You have unsaved changes" : "No changes yet — edit any field"}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={cancelEdit} startIcon={<FaTimes />}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, borderColor: "#cbd5e1", color: "#475569" }}>
                Cancel
              </Button>
              <Button variant="contained" onClick={save} disabled={saving || !dirty} startIcon={saving ? null : <FaSave />}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, px: 3, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </Stack>
          </Paper>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError(null)}>{error}</Alert>}
    </MainContainer>
  );
};

export default EmployeeProfile;